import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { ChangeEditionSectionMessageInspectPermissions } from '@confluence/change-edition/entry-points/ShareAndRestrictDialogUpsells';
import { SPACE_PERMISSIONS_SETTINGS } from '@confluence/named-routes/entry-points/routes';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys';
import { ReverseTrialLozengeExperiment } from '@confluence/trial-components/entry-points/ReverseTrialLozenge';

import {
	CONTENT_PERMS_MANAGEMENT_DOCUMENTATION,
	SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION,
	SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
} from '../../../controller/Controller';
import { USDFeedbackCollector } from '../footer/USDFeedbackCollector';

export type InspectPermsDialogProps = {
	contentId: string;
	onClose: () => void;
};

const i18n = defineMessages({
	inspectPermissions: {
		id: 'share-and-restrict-dialog.overflow-menu.inspect-permissions',
		defaultMessage: 'Inspect permissions',
		description:
			'Text for the share and restrict dialog overflow menu item that allows a user to inspect permissions',
	},
	learnMore: {
		id: 'share-and-restrict-dialog.overflow-menu.learn-more',
		defaultMessage: 'Learn more about restrictions',
		description:
			'Text for the share and restrict dialog overflow menu item that allows a user to learn more about restrictions',
	},
	manageSpace: {
		id: 'share-and-restrict-dialog.overflow-menu.manage-space',
		defaultMessage: 'Manage space access',
		description:
			'Text for the share and restrict dialog overflow menu item that allows a user to manage space access',
	},
	moreActions: {
		id: 'share-and-restrict-dialog.overflow-menu.more',
		defaultMessage: 'More actions',
		description: 'Label for the share and restrict dialog overflow menu item icon',
	},
	giveFeedbackLink: {
		id: 'share-and-restrict-dialog.give-feedback-icon',
		defaultMessage: 'Give feedback',
		description: 'Text for label for opening feedback collector',
	},
});

export type OverflowMenuProps = {
	spaceKey: string;
	isSpaceAdmin: boolean;
	edition: ConfluenceEdition;
	contentId: string;
	openInspectPermsDialog: () => void;
};

const wrapperStyles = xcss({
	width: '254px',
});

export const OverflowMenu = ({
	spaceKey,
	isSpaceAdmin,
	edition,
	openInspectPermsDialog,
	contentId,
}: OverflowMenuProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isFeedbackCollectorOpen, setIsFeedbackCollectorOpen] = useState(false);
	const [displayFeedbackCollectorFlag, setDisplayFeedbackCollectorFlag] = useState(false);

	const showInspectPerms =
		edition && edition !== ConfluenceEdition.FREE && edition !== ConfluenceEdition.STANDARD;

	const handleUpsell = (e: any) => {
		e.stopPropagation();
	};

	const handleInspectPermsClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'inspectPermissions',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			},
		}).fire();
		openInspectPermsDialog();
	}, [createAnalyticsEvent, openInspectPermsDialog]);

	const handleLearnMoreClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowLearnMore',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						icon={MoreIcon}
						label={intl.formatMessage(i18n.moreActions)}
						appearance="subtle"
						ref={triggerRef}
					/>
				)}
				shouldRenderToParent
				placement="bottom-end"
			>
				<DropdownItemGroup>
					{showInspectPerms ? (
						<DropdownItem onClick={handleInspectPermsClick}>
							{intl.formatMessage(i18n.inspectPermissions)}
							<ReverseTrialLozengeExperiment
								edition={ConfluenceEdition.PREMIUM}
								touchpointId="confluence_inspect_permissions_overflow_list_item"
							/>
						</DropdownItem>
					) : (
						<DropdownItem onClick={handleUpsell}>
							<Stack xcss={wrapperStyles}>
								<ChangeEditionSectionMessageInspectPermissions />
							</Stack>
						</DropdownItem>
					)}
				</DropdownItemGroup>
				<DropdownItemGroup hasSeparator={!showInspectPerms}>
					<DropdownItem
						onClick={handleLearnMoreClick}
						href={
							fg('confluence_frontend_unified_restrict_and_share_ga')
								? SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION
								: CONTENT_PERMS_MANAGEMENT_DOCUMENTATION
						}
						target="_blank"
					>
						{intl.formatMessage(i18n.learnMore)}
					</DropdownItem>
					{isSpaceAdmin && (
						<DropdownItem
							href={SPACE_PERMISSIONS_SETTINGS.toUrl({
								spaceKey,
								tab: SpacePermissionsTabKey.Users,
							})}
							target="_blank"
						>
							{intl.formatMessage(i18n.manageSpace)}
						</DropdownItem>
					)}
				</DropdownItemGroup>
				<DropdownItemGroup hasSeparator={!showInspectPerms}>
					<DropdownItem
						onClick={() => {
							setIsFeedbackCollectorOpen(true);
						}}
					>
						<Inline space="space.100" alignBlock="center">
							<FeedbackIcon label="" />
							{intl.formatMessage(i18n.giveFeedbackLink)}
						</Inline>
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>

			{isFeedbackCollectorOpen && (
				<USDFeedbackCollector
					contentId={contentId}
					onClose={() => {
						setIsFeedbackCollectorOpen(false);
					}}
					onSubmit={() => setDisplayFeedbackCollectorFlag(true)}
				/>
			)}
			<FlagGroup onDismissed={() => setDisplayFeedbackCollectorFlag(false)}>
				{displayFeedbackCollectorFlag && <FeedbackFlag />}
			</FlagGroup>
		</>
	);
};
